// utils/queryString.ts
interface Room {
    adults: number;
    children_ages: (number | null)[];
    no_of_infants?: number;
  }
  
  interface Location {
    name: string;
    code: string | number;
    type: string;
    label?: string;
    status?: number;
    exactMatchBoost?: number;
    priority?: number;
  }
  
  interface SearchPayload {
    // 
    location: Location;
    dates: string[];
    guests: Room[];
    currency: string;
    nationality: string;
    hotel_code?: string | number;
    search_id?: string;
    [key: string]: any;
  }
  
  /**
   * Creates a query string from a search payload for use in URLs.
   * @param payload The search payload object
   * @param options Optional configuration
   * @returns A URL-encoded query string
   */
 
// Custom encoding function to use + instead of %20 for spaces
export const encodeWithPlus = (str: string): string => {
    return str.replace(/ /g, "+"); // Replace spaces with +, no other encoding
  };
  
  export const  makeQueryString = (
    payload: SearchPayload,
    options: { prefix?: string } = {}
  ): string => {
    const { prefix = "" } = options;
    const params: string[] = [];
  
    // Location
    params.push(`${prefix}location=${encodeWithPlus(payload?.location?.name)}`);
    params.push(`${prefix}code=${encodeWithPlus(payload?.location?.code.toString())}`);
    params.push(`${prefix}type=${encodeWithPlus(payload?.location?.type)}`);
    if (payload?.location?.label) {
      params.push(`${prefix}label=${encodeWithPlus(payload?.location?.label)}`);
    }
    /*if (payload?.location?.status !== undefined) {
      params.push(`${prefix}status=${encodeWithPlus(payload?.location?.status.toString())}`);
    }
    if (payload?.location?.exactMatchBoost !== undefined) {
      params.push(`${prefix}exactMatchBoost=${encodeWithPlus(payload?.location?.exactMatchBoost.toString())}`);
    }
    if (payload?.location?.priority !== undefined) {
      params.push(`${prefix}priority=${encodeWithPlus(payload?.location?.priority.toString())}`);
    }*/
  
    // Dates
    const datesStr = payload?.dates.join(",");
    params.push(`${prefix}dates=${encodeWithPlus(datesStr)}`);
  
    // Guests
    const adultsStr = payload?.guests?.map((g) => g?.adults).join(",");
    const childrenAgesStr = payload?.guests
      .map((g) => (g?.children_ages?.length > 0 ? g?.children_ages?.join("-") : ""))
      .join(",");
    params.push(`${prefix}adults=${encodeWithPlus(adultsStr)}`);
    if (childrenAgesStr) {
      params.push(`${prefix}childrenAges=${encodeWithPlus(childrenAgesStr)}`);
    }
    /*const infantsStr = payload?.guests?.map((g) => g?.no_of_infants).join(",");
    const totalInfants = payload?.guests.reduce((sum, guest) => sum + (guest?.no_of_infants || 0), 0);

    console.log("infantsStr", totalInfants, infantsStr);
    if(totalInfants > 0){
      params.push(`${prefix}infants=${encodeWithPlus(infantsStr)}`);
    }*/
   

    // Currency and Nationality
    params.push(`${prefix}currency=${encodeWithPlus(payload?.currency)}`);
    params.push(`${prefix}nationality=${encodeWithPlus(payload?.nationality)}`);
    
    // Handle filters specifically
  if (payload.filters && Array.isArray(payload.filters)) {
    payload.filters.forEach((filter: any) => {
      const filterKey = `${prefix}${filter.filterType}`;
      
      if (Array.isArray(filter.value)) {
        // For array values (amenities, rating, meal_plan)
        params.push(`${filterKey}=${encodeWithPlus(filter.value.join(","))}`);
      } else if (typeof filter.value === 'object' && filter.value !== null) {
        // For object values (sortBy, price)
        if (filter.filterType === 'sortBy') {
          params.push(`${filterKey}=${encodeWithPlus(filter.value.value)}`);
        } else if (filter.filterType === 'price') {
          params.push(`${filterKey}=${encodeWithPlus(`${filter.value.from}-${filter.value.to}`)}`);
        }
      }
    });
  }

    // Handle additional fields dynamically
    // Handle other additional fields
  Object.keys(payload).forEach((key) => {
    if (!["location", "dates", "guests", "currency", "nationality", "filters"].includes(key)) {
      const value = payload[key];
      if (Array.isArray(value)) {
        params.push(`${prefix}${key}=${encodeWithPlus(value.join(","))}`);
      } else if (value !== undefined && value !== null) {
        params.push(`${prefix}${key}=${encodeWithPlus(value.toString())}`);
      }
    }
  });
  
    return params.join("&");
  };
 
  
  export const parseQueryString = (
    input: URLSearchParams | { [key: string]: string | string[] | undefined },
    options: { prefix?: string } = {}
  ): SearchPayload => {
    const { prefix = "" } = options;
  
    // Determine if input is URLSearchParams or plain object and create a getter function
    const isURLSearchParams = input instanceof URLSearchParams;
    
    const getString = (key: string): string => {
      if (isURLSearchParams) {
        return (input as URLSearchParams).get(`${prefix}${key}`) || "";
      }
      const value = (input as { [key: string]: string | string[] | undefined })[`${prefix}${key}`];
      return Array.isArray(value) ? (value[0] || "") : (value || "");
    };
  
    // Extract basic fields
    const location: string = getString("location");
    const code = getString("code");
    const type: string = getString("type");
    const label: string = getString("label");
    // const status = getString("status");
    // const exactMatchBoost = getString("exactMatchBoost");
    // const priority = getString("priority");
  
    const dates = getString("dates")?.split(",") || [];
    const adults = getString("adults")?.split(",").map(Number) || [];
    const childrenAgesRaw = getString("childrenAges") || "";
    const childrenAges = childrenAgesRaw
      ? childrenAgesRaw.split(",").map((ages) => (ages ? ages.split("-").map(Number) : []))
      : adults.map(() => []);
    // const no_of_infants = getString("infants")?.split(",") || []; 
  
    const currency: string = getString("currency");
    const nationality: string = getString("nationality");
    const search_id: string = getString("search_id");
    const hotel_code: string | number = getString("hotel_code");
    const city_code: string | number = getString("city_code");
    const property_name: string = getString("property_name");
    const property_code: string | number = getString("property_code");
    const east = getString("east");
    const west = getString("west");
    const north = getString("north");
    const south = getString("south");
    const geolocation = getString("geolocation");
    const radius = getString("radius");
    
    

    // Construct base payload
    const payload: SearchPayload = {
      location: {
        name: location,
        code: code,
        type: type,
        label: label,
        // ...(status && { status: Number(status) }),
        // ...(exactMatchBoost && { exactMatchBoost: Number(exactMatchBoost) }),
        // ...(priority && { priority: Number(priority) }),
      },
      dates,
      guests: adults.map((adultCount, index) => ({
        adults: adultCount,
        children_ages: childrenAges[index] || [],
        // ...no_of_infants && { no_of_infants: Number(no_of_infants[index]) && no_of_infants[index] != "" ? Number(no_of_infants[index]) : undefined }
      })),
      currency,
      nationality,
      ...(hotel_code && { hotel_code: Number(hotel_code) }),
      ...(search_id && { search_id: search_id }),
      ...(city_code && { city_code: Number(city_code) }),
      ...(property_name && { property_name: property_name }),
      ...(property_code && { property_code: Number(property_code) }),

      ...(east && { east: Number(east) }),
      ...(west && { west: Number(west) }),
      ...(north && { north: Number(north) }),
      ...(south && { south: Number(south) }),
      ...(geolocation && { geolocation: geolocation }),
      ...(radius && { radius: Number(radius) }),
    };
  
    // Parse filters
    const filterTypes = ['sortBy', 'amenities', 'price', 'rating', 'meal_plan'];
    filterTypes.forEach(filterType => {
      const value = getString(filterType);
      if (value) {
        let filterValue: any;
        switch (filterType) {
          case 'sortBy':
            filterValue = { filterType: 'sortBy', title: 'Sort By', value };
            break;
          case 'price':
            const [from, to] = value.split('-').map(Number);
            filterValue = { from, to };
            break;
          default:
            filterValue = value.split(',');
        }
        payload.filters!.push({
          filterType,
          value: filterValue
        });
      }
    });
  
    return payload;
  };
   